import { ButcherHeader } from '@components/ButcherHeader';
import { SideNav } from '@components/OrderCreation/OrderSideNav';
import { useEffect, useState } from 'react';
import {
  AddNewCustomerCrumbSteps,
  createCustomerBreadCrumbs,
  errorMessage,
  formRegex,
  responseTexts,
} from '@helpers/Constants';
import { OrderBreadcrumb } from '@components/OrderCreation/OrderBreadcrumbs';
import { Axios } from '@helpers/ApiService';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import {
  useParams,
  useNavigate,
  useLocation,
  generatePath,
} from 'react-router-dom';
import { useToast } from '@contexts/Toast';
import {
  State,
  AddressData,
  CustomerData,
  License,
  CustomerRequiredDetailsFields,
} from '@helpers/Models';
import { OrderListBreadcrumbs } from '@components/OrderProcessing/OrderListBreadcrumbs';
import { formatDate } from '@services/OrderCreationService';
import { customerRequiredLabels } from '@helpers/Constants/Labels';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import TextField from '@mui/material/TextField';
import { useInputValidation } from '../../Hooks/useInputValidation';
import { AxiosError } from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTrash } from '@fortawesome/free-solid-svg-icons';

export const EditCustomer: React.FC = () => {
  const { showToastMessage } = useToast();
  const navigate = useNavigate();
  const { customerId } = useParams<{ customerId?: string }>();
  const [states, setStates] = useState<State[]>([]);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Address state for ZIP code lookup functionality
  const [customerAddress, setCustomerAddress] = useState({
    city: { id: 0, cityName: '' },
    state: { id: 0, stateName: '' },
    postalCode: { id: 0, postalCode: '' },
  });

  // Local state for ZIP code validation error (since hook doesn't have setError)
  const [zipCodeError, setZipCodeError] = useState('');

  // ZIP code validation hook
  const zipCodeValidation = useInputValidation(
    'zipCode',
    customerAddress.postalCode.postalCode,
  );

  // Country code state
  const [countryCode, setCountryCode] = useState('+1');

  const [customer, setCustomer] = useState<CustomerData>({
    firstName: '',
    lastName: '',
    primaryEmail: '',
    phoneHome: '',
    postalCodeId: 0,
    address1: '',
    address2: '',
    address3: '',
    stateId: undefined,
    cityId: 0,
    license: [],
  });
  const [loading, setLoading] = useState(true);
  const [editingLicenseIndex, setEditingLicenseIndex] = useState<number | null>(
    null,
  );
  const location = useLocation();
  const fromModal = location.state?.fromModal;
  const fromList = location.state?.fromList;

  // Modal state
  const [isLicenseModalOpen, setIsLicenseModalOpen] = useState(false);
  const [licenseFormData, setLicenseFormData] = useState<License>({
    id: -1,
    harvestStateId: 0,
    licenseNumber: '',
    expiryDate: '',
    firstName: '',
    lastName: '',
  });
  const [modalErrors, setModalErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    fetchStates();
    if (customerId) {
      fetchCustomerDetails();
    } else {
      setLoading(false);
    }
  }, [customerId]);

  const fetchStates = async () => {
    try {
      const response = await Axios.get(ApiEndpoints.FETCH_STATES);
      setStates(response.data.data);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  // Sync ZIP code validation hook with customerAddress state
  useEffect(() => {
    if (customerAddress.postalCode.postalCode !== zipCodeValidation.value) {
      zipCodeValidation.setValue(customerAddress.postalCode.postalCode);
    }
  }, [customerAddress.postalCode.postalCode, zipCodeValidation]);

  /**
   * Handles ZIP code lookup when user enters 5 digits
   */
  const handleZipCodeLookup = async (zipCode: string) => {
    if (zipCode.length === 5 && /^\d{5}$/.test(zipCode)) {
      try {
        const response = await Axios.post(ApiEndpoints.GET_STATE_CITY_POSTAL, {
          postalCode: zipCode,
        });

        if (response.data.status === 'success') {
          const addressData: AddressData = response.data.data;

          if (
            addressData.cities &&
            addressData.cities.length > 0 &&
            addressData.states &&
            addressData.states.length > 0 &&
            addressData.postalCodes &&
            addressData.postalCodes.length > 0
          ) {
            const city = addressData.cities[0];
            const state = addressData.states[0];
            const postalCodeData = addressData.postalCodes[0];

            setCustomerAddress({
              city: city,
              state: state,
              postalCode: postalCodeData,
            });

            setCustomer((prev) => ({
              ...prev,
              stateId: state.id,
              cityId: city.id,
              postalCodeId: postalCodeData.id,
            }));

            // Clear any previous ZIP code error
            setZipCodeError('');
            showToastMessage(
              `Address found: ${city.cityName}, ${state.stateName}`,
              'success',
            );
          } else {
            // No address found - set error
            setZipCodeError('Invalid postal code. No address found.');
            showToastMessage('ZIP code not found', 'error');
          }
        } else {
          setZipCodeError('Invalid postal code.');
          showToastMessage('Failed to lookup ZIP code', 'error');
        }
      } catch (error) {
        if (error instanceof AxiosError) {
          const msg = error.response?.data?.message || 'Error fetching address data';
          setZipCodeError(msg);
          showToastMessage(msg, 'error');
        } else {
          setZipCodeError('Error looking up ZIP code');
          showToastMessage('Error looking up ZIP code', 'error');
        }
      }
    } else if (zipCode.length === 0) {
      // Clear error if user clears the field
      setZipCodeError('');
    }
  };

  const fetchCustomerDetails = async () => {
    try {
      const response = await Axios.post(`${ApiEndpoints.FETCH_CUSTOMERS}`, {
        id: Number(customerId),
      });
      const customerData = response.data.data.customers[0];

      let hunterLicense: License[] = [];
      if (customerData.HuntingLicences.length > 0) {
        hunterLicense = customerData.HuntingLicences.map(
          (license: Partial<License>) => ({
            id: license.id ?? -1,
            harvestStateId: license.harvestStateId ?? 0,
            licenseNumber: license.licenseNumber ?? '',
            expiryDate: license.expiryDate
              ? new Date(license.expiryDate).toISOString().split('T')[0]
              : '',
            firstName: license.firstName ?? customerData.firstName,
            lastName: license.lastName ?? customerData.lastName,
          }),
        );
      }
      setCustomer({
        id: customerData.id,
        firstName: customerData.firstName,
        lastName: customerData.lastName,
        primaryEmail: customerData.primaryEmail,
        phoneHome: customerData.phoneHome,
        postalCodeId: customerData.postalCodeId ?? '',
        address1: customerData.address1 ?? '',
        address2: customerData.address2 ?? '',
        address3: customerData.address3 ?? '',
        stateId: customerData.stateId ?? 0,
        cityId: customerData.cityId ?? 0,
        license: hunterLicense.length > 0 ? hunterLicense : [],
      });

      if (customerData.countryCode) {
        setCountryCode(customerData.countryCode);
      }

      // Do not block initial form render on postal details lookup.
      setLoading(false);

      if (customerData.postalCodeId) {
        void (async () => {
          try {
            const addressResponse = await Axios.post(
              ApiEndpoints.GET_STATE_CITY_POSTAL,
              {
                postalCodeId: customerData.postalCodeId,
              },
            );

            if (addressResponse.data.status === 'success') {
              const addressData: AddressData = addressResponse.data.data;
              const city = addressData.cities?.find(
                (c) => c.id === customerData.cityId,
              ) || { id: 0, cityName: '' };
              const state = addressData.states?.find(
                (s) => s.id === customerData.stateId,
              ) || { id: 0, stateName: '' };
              const postalCode = addressData.postalCodes?.find(
                (p) => p.id === customerData.postalCodeId,
              ) || { id: 0, postalCode: '' };

              setCustomerAddress({
                city: city,
                state: state,
                postalCode: postalCode,
              });

              zipCodeValidation.setValue(postalCode.postalCode);
            }
          } catch (addressError) {
            console.error('Error fetching address details:', addressError);
          }
        })();
      }
    } catch (error) {
      showToastMessage(errorMessage.ERROR_FETCHING_CUSTOMERS, 'error');
      setLoading(false);
    }
  };

  const handleZipCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    zipCodeValidation.handleChange(e);
    setCustomerAddress((prev) => ({
      ...prev,
      postalCode: { ...prev.postalCode, postalCode: value },
    }));

    if (value.length === 5 && /^\d{5}$/.test(value)) {
      handleZipCodeLookup(value);
    } else if (value.length < 5) {
      // Clear address data if zip is incomplete
      setCustomerAddress(() => ({
        city: { id: 0, cityName: '' },
        state: { id: 0, stateName: '' },
        postalCode: { id: 0, postalCode: value },
      }));
      setCustomer((prev) => ({
        ...prev,
        stateId: 0,
        cityId: 0,
        postalCodeId: 0,
      }));
      setZipCodeError('');
    }
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'city') {
      setCustomerAddress((prev) => ({
        ...prev,
        city: { ...prev.city, cityName: value },
      }));
    } else if (name === 'state') {
      setCustomerAddress((prev) => ({
        ...prev,
        state: { ...prev.state, stateName: value },
      }));
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    if (name === 'countryCode') {
      setCountryCode(value);
      setCustomer((prev) => ({
        ...prev,
        countryCode: value,
      }));
      return;
    }

    let sanitizedValue = value;
    if (name === 'firstName' || name === 'lastName') {
      sanitizedValue = value.replace(/\d/g, '');
    }

    setCustomer((prev) => {
      const updated = {
        ...prev,
        [name]:
          name === 'stateId' || name === 'cityId' || name === 'postalCodeId'
            ? Number(sanitizedValue)
            : sanitizedValue,
      };

      const error = validateField(name, sanitizedValue);
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: error,
      }));

      return updated;
    });
  };

  const validateField = (name: string, value: string): string => {
    const rules: { [key: string]: (value: string) => string } = {
      firstName: (val) => {
        if (!val.trim()) return errorMessage.FIELD_REQUIRED;
        if (/\d/.test(val)) return 'Name cannot contain numbers';
        if (!formRegex.EMOJI.test(val)) return errorMessage.INVALID_CHARACTERS;
        if (!formRegex.HTMLTags.test(val))
          return errorMessage.INVALID_HTML_TAGS;
        return '';
      },
      lastName: (val) => {
        if (!val.trim()) return errorMessage.FIELD_REQUIRED;
        if (/\d/.test(val)) return 'Name cannot contain numbers';
        if (!formRegex.EMOJI.test(val)) return errorMessage.INVALID_CHARACTERS;
        if (!formRegex.HTMLTags.test(val))
          return errorMessage.INVALID_HTML_TAGS;
        return '';
      },
      phoneHome: (val) => {
        if (!val.trim()) return errorMessage.FIELD_REQUIRED;
        if (!formRegex.MOBILE_NUMBER.test(val))
          return errorMessage.INVALID_CONTACT_NUMBER;
        return '';
      },
      cityId: (val) => {
        if (!val.trim()) return errorMessage.FIELD_REQUIRED;
        return '';
      },
      postalCodeId: (val) => {
        if (!val.trim()) return errorMessage.FIELD_REQUIRED;
        if (!formRegex.POSTALCODE.test(val))
          return errorMessage.INVALID_POSTALCODE;
        return '';
      },
      address1: (val) => {
        if (!val.trim()) return '';
        if (val.length > 255) return 'Address1 cannot exceed 255 characters.';
        if (!formRegex.EMOJI.test(val)) return errorMessage.INVALID_CHARACTERS;
        if (!formRegex.HTMLTags.test(val))
          return errorMessage.INVALID_HTML_TAGS;
        return '';
      },
      address2: (val) => {
        if (val.length > 255) return 'Address2 cannot exceed 255 characters.';
        if (!formRegex.EMOJI.test(val)) return errorMessage.INVALID_CHARACTERS;
        if (!formRegex.HTMLTags.test(val))
          return errorMessage.INVALID_HTML_TAGS;
        return '';
      },
      address3: (val) => {
        if (val.length > 255) return 'Address3 cannot exceed 255 characters.';
        if (!formRegex.EMOJI.test(val)) return errorMessage.INVALID_CHARACTERS;
        if (!formRegex.HTMLTags.test(val))
          return errorMessage.INVALID_HTML_TAGS;
        return '';
      },
    };

    return rules[name] ? rules[name](value) : '';
  };

  // ========== License Modal Handlers ==========

  const openAddLicenseModal = () => {
    setLicenseFormData({
      id: -1,
      harvestStateId: 0,
      licenseNumber: '',
      expiryDate: '',
      firstName: customer.firstName || '',
      lastName: customer.lastName || '',
    });
    setModalErrors({});
    setEditingLicenseIndex(null);
    setIsLicenseModalOpen(true);
  };

  const openEditLicenseModal = (index: number) => {
    const license = customer.license[index];
    setLicenseFormData({ ...license });
    setModalErrors({});
    setEditingLicenseIndex(index);
    setIsLicenseModalOpen(true);
  };

  const closeLicenseModal = () => {
    setIsLicenseModalOpen(false);
    setEditingLicenseIndex(null);
    setModalErrors({});
  };

  const saveLicenseFromModal = () => {
    const { firstName, lastName, harvestStateId, licenseNumber, expiryDate } =
      licenseFormData;

    const newErrors: { [key: string]: string } = {};
    if (!firstName?.trim()) newErrors.firstName = errorMessage.FIELD_REQUIRED;
    if (/\d/.test(firstName || '')) newErrors.firstName = 'Name cannot contain numbers';
    if (!lastName?.trim()) newErrors.lastName = errorMessage.FIELD_REQUIRED;
    if (/\d/.test(lastName || '')) newErrors.lastName = 'Name cannot contain numbers';
    if (!harvestStateId) newErrors.harvestStateId = errorMessage.FIELD_REQUIRED;
    if (!licenseNumber?.trim())
      newErrors.licenseNumber = errorMessage.FIELD_REQUIRED;
    if (!expiryDate?.trim()) newErrors.expiryDate = errorMessage.FIELD_REQUIRED;

    if (Object.keys(newErrors).length > 0) {
      setModalErrors(newErrors);
      return;
    }

    setCustomer((prev) => {
      const updatedLicenses = [...prev.license];
      if (editingLicenseIndex !== null) {
        updatedLicenses[editingLicenseIndex] = {
          ...licenseFormData,
          expiryDate: licenseFormData.expiryDate,
        };
      } else {
        updatedLicenses.push({
          ...licenseFormData,
          id: -1,
        });
      }
      return {
        ...prev,
        license: updatedLicenses,
      };
    });

    showToastMessage(
      editingLicenseIndex !== null
        ? 'License updated successfully'
        : responseTexts.LICENSE_CREATION_SUCCESS,
      'success',
    );
    closeLicenseModal();
  };

  const removeLicense = (index: number) => {
    if (customer.license.length <= 1) {
      showToastMessage('Cannot remove the last license.', 'error');
      return;
    }

    setCustomer((prev) => {
      const updatedLicenses = [...prev.license];
      updatedLicenses.splice(index, 1);
      return {
        ...prev,
        license: updatedLicenses,
      };
    });
    showToastMessage('License removed', 'success');
  };

  // ========== Main Form Submit ==========

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // --- Validation checks ---
    const hasFieldError = Object.values(errors).some((errorMsg) => errorMsg.trim() !== '');
    if (hasFieldError) {
      showToastMessage('Please fix all field errors before submitting.', 'error');
      return;
    }

    // Check local ZIP code error
    if (zipCodeError) {
      showToastMessage(zipCodeError, 'error');
      return;
    }

    // Check hook's validation error (if any)
    if (zipCodeValidation.error) {
      showToastMessage(zipCodeValidation.error, 'error');
      return;
    }

    const hasRequiredField = customerRequiredLabels.some((field) => {
      const value = (customer as CustomerRequiredDetailsFields)[field];
      return value === undefined || value === '';
    });
    if (hasRequiredField) {
      showToastMessage('Please fill all the required fields.', 'error');
      return;
    }

    setLoading(true);

    try {
      const { license, ...customerData } = customer;

      const normalizeTextToNull = (value?: string | null) => {
        if (!value) return null;
        const trimmed = value.trim();
        return trimmed.length > 0 ? trimmed : null;
      };

      const hasAddressInput = Boolean(
        normalizeTextToNull(customerData.address1) ||
        normalizeTextToNull(customerData.address2) ||
        normalizeTextToNull(customerData.address3) ||
        normalizeTextToNull(zipCodeValidation.value),
      );

      const customerPayload = {
        ...customerData,
        address1: normalizeTextToNull(customerData.address1),
        address2: customerData.address2?.trim() || '',
        address3: normalizeTextToNull(customerData.address3),
        stateId: hasAddressInput && (customerData.stateId || 0) > 0 ? customerData.stateId : null,
        cityId: hasAddressInput && customerData.cityId > 0 ? customerData.cityId : null,
        postalCodeId:
          hasAddressInput && customerData.postalCodeId > 0
            ? customerData.postalCodeId
            : null,
      };

      const hasPartiallyFilledLicense = license.some((licenseItem) => {
        const isComplete =
          licenseItem.firstName?.trim() &&
          licenseItem.lastName?.trim() &&
          licenseItem.harvestStateId &&
          licenseItem.licenseNumber?.trim() &&
          licenseItem.expiryDate?.trim();

        const hasLicenseData =
          licenseItem.harvestStateId > 0 ||
          licenseItem.licenseNumber?.trim() ||
          licenseItem.expiryDate?.trim();

        return hasLicenseData && !isComplete;
      });

      if (hasPartiallyFilledLicense) {
        showToastMessage(errorMessage.LICENSE_VALIDATION_FAILURE, 'error');
        setLoading(false);
        return;
      }

      const validLicenses = license.filter((licenseItem) => {
        return (
          licenseItem.firstName?.trim() &&
          licenseItem.lastName?.trim() &&
          licenseItem.harvestStateId &&
          licenseItem.licenseNumber?.trim() &&
          licenseItem.expiryDate?.trim()
        );
      });

      const licenseData = validLicenses.map((licenseItem) => {
        let formattedDate = licenseItem.expiryDate;
        try {
          if (!licenseItem.expiryDate.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
            formattedDate = dayjs(licenseItem.expiryDate).format('MM/DD/YYYY');
          }
        } catch (error) {
          formattedDate = licenseItem.expiryDate;
        }
        return {
          ...licenseItem,
          expiryDate: formattedDate,
        };
      });

      const response = await Axios.post(ApiEndpoints.UPSERT_CUSTOMER, {
        customer: customerPayload,
        license: licenseData,
      });

      showToastMessage(
        customerId ? 'Customer updated successfully' : responseTexts.CUSTOMER_CREATION_SUCCESS,
        'success',
      );
      navigate(
        generatePath(FrontendRoutes.CUSTOMER_DETAILS, {
          customerId: response.data.data.customer.id,
        }),
      );
    } catch (error) {
      console.error('Error updating customer:', error);
      if (error instanceof AxiosError) {
        const msg = error.response?.data?.errors[0]?.message || error.message;
        showToastMessage(msg, 'error');
      } else {
        showToastMessage(
          customerId ? errorMessage.ERROR_UPDATING_CUSTOMER : 'Error creating customer',
          'error',
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const getStateName = (stateId: number) => {
    const state = states.find((s) => s.id === stateId);
    return state ? state.stateName : '';
  };

  const isEdit = !!customerId;

  const FromAllOrderBreadCrumb = {
    steps: AddNewCustomerCrumbSteps,
    activeItem: 'Add New Customer',
  };

  return (
    <div className="flex h-screen w-screen gap-1">
      <SideNav active={2} />
      <div className="flex-1 p-1 overflow-auto relative">
        <ButcherHeader />
        {fromModal ? (
          fromList === 'all_order' && (
            <OrderListBreadcrumbs orderInfo={FromAllOrderBreadCrumb} />
          )
        ) : (
          <OrderBreadcrumb
            steps={createCustomerBreadCrumbs}
            activeItem="Customers"
          />
        )}
        <div className="flex items-center justify-between mt-4 p-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2 cursor-pointer bg-transparent border-none p-0"
          >
            <svg
              width="8"
              height="14"
              viewBox="0 0 8 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6.41504 13.5229L0.785645 7.64746C0.631836 7.46289 0.570312 7.27832 0.570312 7.09375C0.570312 6.93994 0.631836 6.75537 0.754883 6.60156L6.38428 0.726074C6.66113 0.418457 7.15332 0.418457 7.43018 0.695312C7.73779 0.972168 7.73779 1.43359 7.46094 1.74121L2.32373 7.09375L7.4917 12.5078C7.76855 12.7847 7.76855 13.2769 7.46094 13.5537C7.18408 13.8306 6.69189 13.8306 6.41504 13.5229Z"
                fill="#000A16"
              />
            </svg>
            <span className="text-lg font-semibold rounded-md">
              {isEdit ? 'Update Customer' : 'Add New Customer'}
            </span>
          </button>
        </div>
        <div>
          <form onSubmit={handleSubmit}>
            {/* Basic info card */}
            <div className="rounded-bl-xxl mt-2 rounded-br-xl shadow-lg ml-2 mr-4">
              <h2 className="text-lg font-semibold rounded-md ml-2 px-6 mb-4">
                Basic Info
              </h2>
              <div className="shadown-lg rounded-tl-lg rounded-tr-lg px-8 bg-white pb-8">
                <div className="flex w-full justify-start">
                  <div className="w-1/2">
                    <label htmlFor="firstName" className="block mb-4">
                      First Name <sup>*</sup>
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={customer.firstName}
                      onChange={handleInputChange}
                      placeholder="First Name"
                      className={`p-2 border rounded-md text-gray-600 w-9/12 ${errors.firstName
                        ? 'outline-red-500 border-red-500 focus:outline-red-500'
                        : 'outline-gray-300 focus:outline-primary'
                        }`}
                    />
                    {errors.firstName && (
                      <p className="text-red-600 text-sm">{errors.firstName}</p>
                    )}
                  </div>
                  <div className="w-1/2">
                    <label htmlFor="lastName" className="block mb-4">
                      Last Name <sup>*</sup>
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={customer.lastName}
                      onChange={handleInputChange}
                      placeholder="Last Name"
                      className={`p-2 border rounded-md text-gray-600 w-9/12 ${errors.lastName
                        ? 'outline-red-500 border-red-500 focus:outline-red-500'
                        : 'outline-gray-300 focus:outline-primary'
                        }`}
                    />
                    {errors.lastName && (
                      <p className="text-red-600 text-sm">{errors.lastName}</p>
                    )}
                  </div>
                </div>
                <div className="flex w-full justify-start mt-4">
                  <div className="w-1/2">
                    <label htmlFor="primaryEmail" className="block mb-4">
                      Email
                    </label>
                    <input
                      type="email"
                      name="primaryEmail"
                      value={customer.primaryEmail}
                      onChange={handleInputChange}
                      placeholder="Email"
                      className={`p-2 border rounded-md text-gray-600 w-9/12 ${errors.primaryEmail
                        ? 'outline-red-500 border-red-500 focus:outline-red-500'
                        : 'outline-gray-300 focus:outline-primary'
                        }`}
                    />
                    {errors.primaryEmail && (
                      <p className="text-red-600 text-sm">
                        {errors.primaryEmail}
                      </p>
                    )}
                  </div>
                  <div className="w-1/2">
                    <label htmlFor="phoneHome" className="block mb-4">
                      Contact <sup>*</sup>
                    </label>
                    <div className="flex items-center space-x-2">
                      <select
                        name="countryCode"
                        value={countryCode}
                        onChange={handleInputChange}
                        className="p-2 border rounded-md text-gray-600 bg-white outline-gray-300 focus:outline-primary"
                      >
                        <option value="+1">+1</option>
                      </select>
                      <input
                        type="tel"
                        name="phoneHome"
                        value={customer.phoneHome}
                        onChange={handleInputChange}
                        placeholder="Contact"
                        className={`p-2 border rounded-md text-gray-600 flex-1 ${errors.phoneHome
                          ? 'outline-red-500 border-red-500 focus:outline-red-500'
                          : 'outline-gray-300 focus:outline-primary'
                          }`}
                      />
                    </div>
                    {errors.phoneHome && (
                      <p className="text-red-600 text-sm">{errors.phoneHome}</p>
                    )}
                  </div>
                </div>
                <div className="flex w-full justify-start mt-4">
                  <div className="w-1/2">
                    <label htmlFor="address1" className="block mb-4">
                      Address Line 1
                    </label>
                    <input
                      type="text"
                      name="address1"
                      value={customer.address1}
                      onChange={handleInputChange}
                      placeholder="Address Line 1"
                      className={`p-2 border rounded-md text-gray-600 w-9/12 ${errors.address1
                        ? 'outline-red-500 border-red-500 focus:outline-red-500'
                        : 'outline-gray-300 focus:outline-primary'
                        }`}
                    />
                    {errors.address1 && (
                      <p className="text-red-600 text-sm">{errors.address1}</p>
                    )}
                  </div>
                  <div className="w-1/2">
                    <label htmlFor="address2" className="block mb-4">
                      Address Line 2
                    </label>
                    <input
                      type="text"
                      name="address2"
                      value={customer.address2}
                      onChange={handleInputChange}
                      placeholder="Address Line 2"
                      className={`p-2 border rounded-md text-gray-600 w-9/12 ${errors.address2
                        ? 'outline-red-500 border-red-500 focus:outline-red-500'
                        : 'outline-gray-300 focus:outline-primary'
                        }`}
                    />
                    {errors.address2 && (
                      <p className="text-red-600 text-sm">{errors.address2}</p>
                    )}
                  </div>
                </div>
                <div className="flex w-full justify-start mt-4">
                  <div className="w-1/2">
                    <label htmlFor="address3" className="block mb-4">
                      Address Line 3
                    </label>
                    <input
                      type="text"
                      name="address3"
                      value={customer.address3}
                      onChange={handleInputChange}
                      placeholder="Address Line 3"
                      className={`p-2 border rounded-md text-gray-600 w-9/12 ${errors.address3
                        ? 'outline-red-500 border-red-500 focus:outline-red-500'
                        : 'outline-gray-300 focus:outline-primary'
                        }`}
                    />
                    {errors.address3 && (
                      <p className="text-red-600 text-sm">{errors.address3}</p>
                    )}
                  </div>
                  <div className="w-1/2">
                    <label htmlFor="postalCode" className="block mb-4">
                      Zipcode
                    </label>
                    <input
                      type="text"
                      name="postalCode"
                      value={zipCodeValidation.value}
                      onChange={handleZipCodeChange}
                      onKeyPress={zipCodeValidation.handleKeyPress}
                      className={`p-2 border rounded-md text-gray-600 w-9/12 ${zipCodeError || zipCodeValidation.error || errors.postalCodeId
                        ? 'outline-red-500 border-red-500 focus:outline-red-500'
                        : 'outline-gray-300 focus:outline-primary'
                        }`}
                      placeholder="Enter 5-digit ZIP code"
                      maxLength={zipCodeValidation.maxLength}
                    />
                    {(zipCodeError || zipCodeValidation.error || errors.postalCodeId) && (
                      <p className="text-red-600 text-sm mt-1">
                        {zipCodeError || zipCodeValidation.error || errors.postalCodeId}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex w-full justify-start mt-4">
                  <div className="w-1/2">
                    <label htmlFor="city" className="block mb-4">
                      City
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={customerAddress.city.cityName}
                      onChange={handleAddressChange}
                      className={`p-2 border rounded-md text-gray-600 w-9/12 ${errors.cityId
                        ? 'outline-red-500 border-red-500 focus:outline-red-500'
                        : 'outline-gray-300 focus:outline-primary'
                        }`}
                      readOnly
                      placeholder="City (auto-filled from ZIP)"
                    />
                    {errors.cityId && (
                      <p className="text-red-600 text-sm">{errors.cityId}</p>
                    )}
                  </div>
                  <div className="w-1/2">
                    <label htmlFor="state" className="block mb-4">
                      State
                    </label>
                    <input
                      type="text"
                      name="state"
                      readOnly
                      value={customerAddress.state.stateName}
                      onChange={handleAddressChange}
                      className={`p-2 border rounded-md text-gray-600 w-9/12 ${errors.stateId
                        ? 'outline-red-500 border-red-500 focus:outline-red-500'
                        : 'outline-gray-300 focus:outline-primary'
                        }`}
                      placeholder="State (auto-filled from ZIP)"
                    />
                    {errors.stateId && (
                      <p className="text-red-600 text-sm">{errors.stateId}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* License card */}
            <div className="rounded-bl-xxl mt-8 rounded-br-xl shadow-lg ml-2 mr-4">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold rounded-md ml-2 px-6 mb-4">
                  License
                </h2>
                <div id="add-license" className="mx-8">
                  <button
                    type="button"
                    onClick={openAddLicenseModal}
                    className="w-full bg-primary text-white px-4 py-2 my-8 rounded-md"
                  >
                    + Add License
                  </button>
                </div>
              </div>

              <div className="py-4">
                {customer.license.length === 0 ? (
                  <div className="mx-8 py-4 text-center text-gray-500">
                    No licenses added yet.
                  </div>
                ) : (
                  customer.license.map((license, index) => (
                    <div
                      key={`license-${index}`}
                      className="bg-[#FFFEFA] mb-8 shadow-lg rounded-lg mx-8 py-8 pt-8 px-8 border border-gray-300"
                    >
                      <div className="py-4">
                        <div className="flex justify-between items-center mb-4">
                          <p className="text-lg">License Details</p>
                          <div className="space-x-4 flex items-center">
                            <button
                              onClick={() => openEditLicenseModal(index)}
                              className="text-primary hover:text-primary-dark transition-colors"
                              type="button"
                              title="Edit License"
                            >
                              <FontAwesomeIcon icon={faPen} />
                            </button>
                            <button
                              onClick={() => removeLicense(index)}
                              className="text-primary hover:text-primary-dark transition-colors"
                              type="button"
                              title="Remove License"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        </div>

                        <div className="flex w-full justify-start items-end mb-4">
                          <div className="w-1/3">
                            <label className="block mb-2 text-sm text-black opacity-25">
                              First Name
                            </label>
                            <p>{license.firstName}</p>
                          </div>

                          <div className="w-1/3">
                            <label className="block mb-2 text-sm text-black opacity-25">
                              Last Name
                            </label>
                            <p>{license.lastName}</p>
                          </div>

                          <div className="w-1/3">
                            <label className="block mb-2 text-sm text-black opacity-25">
                              State
                            </label>
                            <p>{getStateName(license.harvestStateId)}</p>
                          </div>
                        </div>

                        <div className="w-full flex justify-start items-end mb-4">
                          <div className="w-1/3">
                            <label className="block mb-2 text-sm text-black opacity-25">
                              License Number
                            </label>
                            <p>{license.licenseNumber}</p>
                          </div>
                          <div className="w-1/3 relative">
                            <label className="block mb-2 text-sm text-black opacity-25">
                              License Expiry
                            </label>
                            <p>
                              {license.expiryDate &&
                                formatDate(license.expiryDate)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Submit Button */}
            <div className="w-full mt-6 p-4">
              <div className="flex flex-col xl:flex-row xl:justify-end w-full">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full xl:w-[200px] h-11 px-4 bg-primary text-white rounded-md flex items-center justify-center font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span>
                    {loading
                      ? 'Saving...'
                      : isEdit
                        ? 'Update Customer'
                        : 'Create Customer'}
                  </span>
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* License Modal */}
      {isLicenseModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 transition-opacity duration-300 ease-in-out"
          onClick={closeLicenseModal}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 transform transition-all duration-300 ease-in-out scale-100 opacity-100"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center border-b p-4">
              <h3 className="text-xl font-semibold">
                {editingLicenseIndex !== null ? 'Edit License' : 'Add License'}
              </h3>
              <button
                type="button"
                onClick={closeLicenseModal}
                className="text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <div className="flex w-full justify-start items-end mb-4">
                <div className="w-1/3">
                  <label htmlFor="modal-firstName" className="block mb-4">
                    First Name <sup>*</sup>
                  </label>
                  <input
                    id="modal-firstName"
                    type="text"
                    value={licenseFormData.firstName || ''}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\d/g, '');
                      setLicenseFormData({
                        ...licenseFormData,
                        firstName: val,
                      });
                    }}
                    placeholder="First Name"
                    className={`p-2 border rounded-md text-gray-600 w-11/12 focus:outline-primary ${modalErrors.firstName ? 'border-red-500' : ''
                      }`}
                  />
                  {modalErrors.firstName && (
                    <p className="text-red-600 text-sm">
                      {modalErrors.firstName}
                    </p>
                  )}
                </div>

                <div className="w-1/3">
                  <label htmlFor="modal-lastName" className="block mb-4">
                    Last Name <sup>*</sup>
                  </label>
                  <input
                    id="modal-lastName"
                    type="text"
                    value={licenseFormData.lastName || ''}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\d/g, '');
                      setLicenseFormData({
                        ...licenseFormData,
                        lastName: val,
                      });
                    }}
                    placeholder="Last Name"
                    className={`p-2 border rounded-md text-gray-600 w-11/12 focus:outline-primary ${modalErrors.lastName ? 'border-red-500' : ''
                      }`}
                  />
                  {modalErrors.lastName && (
                    <p className="text-red-600 text-sm">
                      {modalErrors.lastName}
                    </p>
                  )}
                </div>

                <div className="w-1/3">
                  <label htmlFor="modal-state" className="block mb-4">
                    State <sup>*</sup>
                  </label>
                  <select
                    id="modal-state"
                    value={licenseFormData.harvestStateId || 0}
                    onChange={(e) =>
                      setLicenseFormData({
                        ...licenseFormData,
                        harvestStateId: parseInt(e.target.value),
                      })
                    }
                    className={`p-2 border rounded-md text-gray-600 w-11/12 focus:outline-primary ${modalErrors.harvestStateId ? 'border-red-500' : ''
                      }`}
                  >
                    <option value={0}>Select state</option>
                    {states.map((state) => (
                      <option key={state.id} value={state.id}>
                        {state.stateName}
                      </option>
                    ))}
                  </select>
                  {modalErrors.harvestStateId && (
                    <p className="text-red-600 text-sm">
                      {modalErrors.harvestStateId}
                    </p>
                  )}
                </div>
              </div>

              <div className="w-full flex justify-start items-end mb-4">
                <div className="w-1/3">
                  <label htmlFor="modal-licenseNumber" className="block mb-4">
                    License Number <sup>*</sup>
                  </label>
                  <input
                    id="modal-licenseNumber"
                    type="text"
                    value={licenseFormData.licenseNumber || ''}
                    onChange={(e) =>
                      setLicenseFormData({
                        ...licenseFormData,
                        licenseNumber: e.target.value,
                      })
                    }
                    placeholder="License Number"
                    className={`p-2 border rounded-md text-gray-600 w-11/12 focus:outline-primary ${modalErrors.licenseNumber ? 'border-red-500' : ''
                      }`}
                  />
                  {modalErrors.licenseNumber && (
                    <p className="text-red-600 text-sm">
                      {modalErrors.licenseNumber}
                    </p>
                  )}
                </div>
                <div className="w-1/3 relative">
                  <label htmlFor="modal-expiryDate" className="block mb-4">
                    License Expiry <sup>*</sup>
                  </label>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      disablePast
                      format="MM/DD/YYYY"
                      value={
                        licenseFormData.expiryDate
                          ? dayjs(licenseFormData.expiryDate)
                          : null
                      }
                      onChange={(newValue) => {
                        if (newValue) {
                          const formattedDate = newValue.format('MM/DD/YYYY');
                          setLicenseFormData({
                            ...licenseFormData,
                            expiryDate: formattedDate,
                          });
                        }
                      }}
                      desktopModeMediaQuery="@media (min-width: 0px)"
                      slots={{ textField: TextField }}
                      slotProps={{
                        textField: {
                          placeholder: 'MM/DD/YYYY',
                          size: 'small',
                          sx: {
                            textDecorationColor: '#4b5563',
                            backgroundColor: 'white',
                            '& .MuiOutlinedInput-notchedOutline': {
                              border: 'none',
                            },
                          },
                          InputProps: {
                            sx: {
                              border: '1px solid #d1d5db',
                              borderRadius: '0.375rem',
                              '&.Mui-focused': {
                                borderColor: '#240089',
                              },
                            },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                  {modalErrors.expiryDate && (
                    <p className="text-red-600 text-sm">
                      {modalErrors.expiryDate}
                    </p>
                  )}
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-4 border-t p-4">
              <button
                type="button"
                onClick={closeLicenseModal}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveLicenseFromModal}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Save License
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};